/**
 * Base API Client
 *
 * Provides a fetch wrapper with JWT token handling,
 * automatic header injection, and error handling.
 */

import { createLogger } from '../utils/logger'

const logger = createLogger('api-client')

// API base URL from environment
// Use relative path in development to leverage vite proxy, or absolute URL in production
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

/**
 * Token storage keys
 */
const TOKEN_KEYS = {
  ACCESS: 'access_token',
  REFRESH: 'refresh_token',
}

/**
 * Get stored access token
 * @returns {string|null}
 */
export function getAccessToken() {
  return localStorage.getItem(TOKEN_KEYS.ACCESS)
}

/**
 * Get stored refresh token
 * @returns {string|null}
 */
export function getRefreshToken() {
  return localStorage.getItem(TOKEN_KEYS.REFRESH)
}

/**
 * Store tokens in localStorage
 * @param {string} accessToken
 * @param {string|null} refreshToken
 */
export function setTokens(accessToken, refreshToken = null) {
  localStorage.setItem(TOKEN_KEYS.ACCESS, accessToken)
  if (refreshToken) {
    localStorage.setItem(TOKEN_KEYS.REFRESH, refreshToken)
  }
}

/**
 * Clear all stored tokens
 */
export function clearTokens() {
  localStorage.removeItem(TOKEN_KEYS.ACCESS)
  localStorage.removeItem(TOKEN_KEYS.REFRESH)
}

/**
 * Check if user has a token stored
 * @returns {boolean}
 */
export function hasToken() {
  return !!getAccessToken()
}

/**
 * API Error class for consistent error handling
 */
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

/**
 * Make an API request with automatic token handling
 *
 * @param {string} endpoint - API endpoint (e.g., '/auth/login')
 * @param {Object} options - Fetch options
 * @param {string} options.method - HTTP method
 * @param {Object} options.body - Request body (will be JSON stringified)
 * @param {Object} options.headers - Additional headers
 * @param {boolean} options.auth - Whether to include auth header (default: true if token exists)
 * @param {Object} options.params - URL query parameters
 * @returns {Promise<any>} Response data
 * @throws {ApiError} On request failure
 */
export async function apiRequest(endpoint, options = {}) {
  const { method = 'GET', body = null, headers = {}, auth = true, params = null } = options

  // Build URL with query parameters
  let url = `${API_BASE_URL}${endpoint}`
  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.append(key, value)
      }
    })
    const queryString = searchParams.toString()
    if (queryString) {
      url += `?${queryString}`
    }
  }

  // Build headers
  const requestHeaders = {
    'Content-Type': 'application/json',
    ...headers,
  }

  // Add auth header if token exists and auth is not explicitly disabled
  const token = getAccessToken()
  if (auth && token) {
    requestHeaders['Authorization'] = `Bearer ${token}`
  }

  // Build request options
  const fetchOptions = {
    method,
    headers: requestHeaders,
  }

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body)
  }

  const startTime = Date.now()

  try {
    logger.debug('API request', { method, endpoint, hasAuth: auth && !!getAccessToken() })

    const response = await fetch(url, fetchOptions)
    const duration = Date.now() - startTime

    // Handle 401 Unauthorized - token expired or invalid
    if (response.status === 401) {
      logger.warn('Unauthorized request', { endpoint, duration_ms: duration })
      clearTokens()
      // Dispatch custom event for auth context to handle
      window.dispatchEvent(new CustomEvent('auth:unauthorized'))
      throw new ApiError('Session expired. Please log in again.', 401)
    }

    // Parse response
    let data = null
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      data = await response.json()
    }

    // Handle error responses
    if (!response.ok) {
      const message =
        data?.detail || data?.message || `Request failed with status ${response.status}`
      logger.error('API error response', {
        endpoint,
        status: response.status,
        message,
        duration_ms: duration,
      })
      throw new ApiError(message, response.status, data)
    }

    logger.debug('API response', { endpoint, status: response.status, duration_ms: duration })
    return data
  } catch (error) {
    const duration = Date.now() - startTime

    // Re-throw ApiErrors
    if (error instanceof ApiError) {
      throw error
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      logger.error('Network error', { endpoint, duration_ms: duration, error: error.message })
      throw new ApiError('Network error. Please check your connection.', 0)
    }

    // Handle other errors
    logger.error('Unexpected API error', { endpoint, duration_ms: duration, error: error.message })
    throw new ApiError(error.message || 'An unexpected error occurred', 0)
  }
}

/**
 * Convenience methods for common HTTP methods
 */
export const api = {
  get: (endpoint, params = null, options = {}) =>
    apiRequest(endpoint, { method: 'GET', params, ...options }),

  post: (endpoint, body = null, options = {}) =>
    apiRequest(endpoint, { method: 'POST', body, ...options }),

  patch: (endpoint, body = null, options = {}) =>
    apiRequest(endpoint, { method: 'PATCH', body, ...options }),

  put: (endpoint, body = null, options = {}) =>
    apiRequest(endpoint, { method: 'PUT', body, ...options }),

  delete: (endpoint, options = {}) => apiRequest(endpoint, { method: 'DELETE', ...options }),
}

export default api
